<template>
    <nav id="navigation-contextual">

        <Toggle @click="handleToggleNavigation" :active="!navigationOpen"/>

        <div id="navigation-contextual-content"
             :class="{ 'is-open': navigationOpen }"
        >
            <div id="primary" v-if="!activeRoute.secondaryMenuActive">
                <div class="top"  v-if="navigationOpen">
                    <router-link to="/" class="ps-icon">
                      <img :src="clientLogoURL" :alt="clientName" v-if="clientLogoURL">
                      <div class="label" v-else>{{clientName}}</div>
                    </router-link>

                    <template v-if="isProjectRoute">
                        <router-link :to="currentProjectsRoute" class="all-projects-link">
                            <o-icon icon="arrow-left" pack="fas" size="small" />
                            <span>All Projects</span>
                        </router-link>
                        <NavMenu secondary v-if="showProjectTabs" class="project-tabs-menu" :menu="projectTabsMenu" />
                        <BidPackagesList class="bid-packages" :project-tabs-menu="projectTabsMenu" />
                    </template>
                    <NavMenu v-else secondary :menu="menu"/>
                </div>
                <div class="bottom" v-if="navigationOpen">
                    <NavMenu secondary :menu="bottomMenu"/>
                    <div id="version">v{{version}}</div>
                </div>
            </div>

            <div id="secondary" v-if="activeRoute.secondaryMenuActive">
                <div v-if="navigationOpen">
                    <NavMenu secondary :menu="secondaryMenu" v-if="secondaryMenu"/>
                </div>
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">

  import { ref, computed } from "vue";
  import { useRoute } from "vue-router";

  import appData from '/package';
  import BidPackagesList from "@/pages/Projects/BidPackagesList.vue";
  import primaryMenu from '@/menus/primary';
  import primaryBottomMenu from '@/menus/primary-bottom';
  import NavMenu from '@/components/NavMenu.vue';
  import Toggle from '@/components/Toggle.vue';

  import {
    Menu,
  } from "@/internal";
  import {storeToRefs} from "pinia";
  import {useCompanyStore} from "@/stores/CompanyStore";
  import {useClientStore} from '@/stores/ClientStore';
  import {useProjectStore} from "@/stores/ProjectStore";
  import {useSystemStore} from "@/stores/SystemStore";

  const { company } = storeToRefs(useCompanyStore());
  const {client} = storeToRefs(useClientStore());
  const { project } = storeToRefs(useProjectStore());
  const system = useSystemStore();
  const user = {};
  const route = useRoute();

  withDefaults(defineProps<{
    dark?: boolean,
  }>(), {
    dark: false,
  });

  let navigationOpen = ref(true);

  const activeRoute = computed(() => {
    return primaryMenu.menuItems && primaryMenu.menuItems.find((menuItem: Record<any, any>) => menuItem.route && route.name && (<string>route.name).startsWith(menuItem.route.name)) || {};
  });

  const clientLogoURL = computed<string | null>(() => client.value?.logo_url || company.value?.client?.logo_url || null);

  const clientName = computed(() => client.value?.name || company.value?.client?.name || 'SubsPortal');

  const isProjectRoute = computed(() => [
    'projects/show',
    'projects/bid-packages/show',
    'projects/past/show',
    'projects/past/bid-packages/show',
  ].includes(String(route.name)));

  const currentProjectsRoute = computed(() => ({ name: 'projects/current' }));

  const showProjectTabs = computed(() => {
    return !route.params.bidPackageId && !project.value?.bid_packages?.length;
  });

  const projectTabsMenu = computed<Menu>(() => {
    const projectId = route.params.projectId;
    const bidPackageId = route.params.bidPackageId;
    const routeName = String(route.name);

    const projectRoute = (hash = '') => ({
      name: routeName,
      params: {
        projectId,
        ...(bidPackageId ? { bidPackageId } : {}),
      },
      hash,
    });

    return new Menu({
      menuItems: [
        { title: 'Bidding', icon: 'hammer', iconPack: 'fal', route: projectRoute() },
        { title: 'Info', icon: 'circle-info', iconPack: 'fas', route: projectRoute('#info') },
        { title: 'Notices', icon: 'bell', iconPack: 'fal', route: projectRoute('#notices') },
        { title: 'Contacts', icon: 'users', iconPack: 'fal', route: projectRoute('#contacts') },
        { title: 'Submit Question', icon: 'messages-question', iconPack: 'fal', route: projectRoute('#question') },
        { title: 'Submit Bid', icon: 'file-upload', iconPack: 'fal', route: projectRoute('#bid') },
      ],
    });
  });

  const menu = computed<Menu>(() => {
    return primaryMenu();
  });

  const bottomMenu = computed<Menu>(() => {
    return primaryBottomMenu({
      handleTakeOffSoftwareClick: () => {
        window.open(`${import.meta.env.VITE_TAKE_OFF_SOFTWARE_URL}`,'_blank');
      },
    });
  });

  const secondaryMenu = computed<Menu | null>(() => {
    if (activeRoute.value.menuItems) {
      return new Menu({
        menuItems: activeRoute.value.menuItems,
      });
    }

    return null;
  });

  const version = computed<string>(() => {
    return appData.version;
  });

  function handleToggleNavigation() {
    navigationOpen.value = !navigationOpen.value;
  }

</script>

<style lang="scss">
    #navigation-contextual {
        display: flex;
        position: relative;
        z-index: 2000;


        &:hover {
            .toggle {
                pointer-events: auto;
                visibility: visible;
            }
        }

      &, .menu-list{
        a {

          border: 1px solid transparent;
          border-left-width: 3px;

          &:not(.ps-icon):hover {
            border-radius: 0 4px 4px 0;
            border-left-color: var(--nav-border-color);
          }

          &.active, &.item-active {
            font-weight: 600;
            border-radius: 0 4px 4px 0;
            border-left-color: var(--nav-border-color);
          }
        }
      }


        .toggle {
            pointer-events: none;
            position: absolute;
            right: -12px;
            top: 12px;
            visibility: hidden;
            z-index: 1;

            &.is-active {
                pointer-events: auto;
                visibility: visible;
            }
        }

        #navigation-contextual-content {
          background: var(--nav-secondary-background);
          color: var(--nav-secondary-text);
          display: flex;
          overflow-x: hidden;
          transition: width .2s;
          width: 0;

          &.is-open {
              width: 250px;
          }

          .bid-packages {
            margin-top: 2em;
            padding-bottom: 1.75em;
          }

          .project-tabs-menu {
            border-left: 1px solid var(--nav-secondary-border-color);
            margin: .75em 0 0 1.75em;
            padding: 0 0 0 .5em;

            .menu-item {
              padding: .45em .75em;
            }
          }
        }

        #primary,
        #secondary {
            flex: 1;
            padding: 15px 12.5px;
        }

        #primary {
            background: var(--nav-secondary-background);
            color:var(--nav-secondary-text);
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .top > .ps-icon {
                align-items: center;
                display: flex;
                justify-content: center;
                outline: none;
                text-decoration: none;
                img {
                    display: block;
                    height: auto;
                    max-height: 150px;
                    max-width: 100%;
                    object-fit: contain;
                }
            }

            #logo {
                color: $white;
                font-family: 'Montserrat', sans-serif;
                font-size: 24px;
                font-weight: 500;
                padding: 5px 15px 0;
            }

            #logo-placeholder {
                height: 48px;
            }

            #version {
                color: var(--nav-secondary-text-muted);
                font-size: 13px;
                margin-top: 10px;
                text-align: center;
            }

            .label{
              color:var(--nav-secondary-text);
            }

            .all-projects-link {
                align-items: center;
                color: var(--nav-secondary-text);
                display: flex;
                gap: .65em;
                margin-top: 1.5em;
                padding: .5em .75em .5em .5em;

                &:hover {
                    background: var(--nav-secondary-background-hover);
                    border-radius: 4px;
                }
            }
        }

        #secondary {
            background: $white-ter;
            border-right: 1px solid $grey-lightest;

            #object-switcher {
                border: 1px solid $grey;
                border-radius: 4px;
                height: 39px;
                margin-bottom: 7.5px;
                overflow: hidden;
                padding: 7.5px 15px;
                text-align: center;
            }

            #object-switcher-placeholder {
                height: 47px;
            }
        }
    }
</style>
